import { Button, Form, Select, Typography } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';
import dayjs, { Dayjs } from 'dayjs';
import React, { ReactElement, useCallback, useState } from 'react';
import WarehouseTypeEnum from '~Api/Warehouse/WarehouseTypeEnum';
import Layout from '~Reports/Layout';
import DatePicker from '~UI/DatePicker';
import './warehouses-fwt-cashflow.less';

interface IErrors {
    dateRange?: string;
    warehouseType?: string;
}

export default function WarehousesFwtCashflow(): ReactElement {
    const [ endDate, setEndDate ] = useState<string>(null);
    const [ errors, setErrors ] = useState<IErrors>({});
    const [ startDate, setStartDate ] = useState<string>(null);
    const [ warehouseType, setWarehouseType ] = useState<WarehouseTypeEnum>(null);

    const validateDateRange: () => boolean = useCallback(() => {
        let error: string;

        if (!startDate || !endDate) {
            error = 'Please enter a date range';
        }

        if (dayjs(endDate).diff(startDate, 'day') > 31) {
            error = 'Please enter dates that span a month at most';
        }

        setErrors((prevErrors: IErrors) => ({
            ...prevErrors,
            dateRange: error,
        }));

        return !error;
    }, [
        endDate,
        startDate,
    ]);

    const onChangeDateRange: (dateRange: [Dayjs, Dayjs]) => void = useCallback((dateRange: [Dayjs, Dayjs]) => {
        setEndDate(dateRange[1].format('YYYY-MM-DD')),
        setStartDate(dateRange[0].format('YYYY-MM-DD'));
    }, []);

    const validateWarehouseType: () => boolean = useCallback(() => {
        let error: string = null;

        if (!warehouseType) {
            error = 'Please select a warehouse';
        }

        setErrors((prevErrors: IErrors) => ({
            ...prevErrors,
            warehouseType: error,
        }));

        return !error;
    }, [
        warehouseType,
    ]);

    const onChangeWarehouseType: (value: string) => void = useCallback((value: string) => {
        setWarehouseType(value as WarehouseTypeEnum);
    }, []);

    const onClickDownload: () => void = useCallback(() => {
        let valid: boolean = true;

        valid = validateDateRange() && valid;
        valid = validateWarehouseType() && valid;

        if (!valid) {
            return;
        }

        window.open(`${process.env.API_HOST}/warehouse-reports/fwt-cashflow?startDate=${startDate}&endDate=${endDate}&warehouseType=${warehouseType}`, '_blank');
    }, [
        endDate,
        startDate,
        validateDateRange,
        validateWarehouseType,
        warehouseType,
    ]);

    const warehouseOptions: DefaultOptionType[] = [
        {
            label: 'Funding Warehouse Trust 1',
            value: WarehouseTypeEnum.Fwt1,
        },
        {
            label: 'Funding Warehouse Trust 2',
            value: WarehouseTypeEnum.Fwt2,
        },
    ];

    return (
        <Layout section='warehouses-fwt-cashflow'>
            <Typography.Title level={2}>Warehouses - FWT Cashflow</Typography.Title>
            <Form.Item
                className='warehouse-type'
                help={errors.warehouseType}
                label='Warehouse'
                validateStatus={errors.warehouseType && 'error'}
            >
                <Select
                    value={warehouseType}
                    onChange={onChangeWarehouseType}
                    onBlur={validateWarehouseType}
                    options={warehouseOptions}
                />
            </Form.Item>
            <Form.Item
                className='date-range'
                help={errors.dateRange}
                label='Date Range'
                validateStatus={errors.dateRange && 'error'}
            >
                <DatePicker.RangePicker
                    picker='date'
                    format='DD/MM/YYYY'
                    onBlur={validateDateRange}
                    onChange={onChangeDateRange}
                    value={[ startDate ? dayjs(startDate) : null, endDate ? dayjs(endDate) : null ]}
                />
            </Form.Item>
            <Form.Item>
                <Button onClick={onClickDownload} type='primary'>Download</Button>
            </Form.Item>
        </Layout>
    );
}
